<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxItem } from 'devextreme-vue/form'
import { DxTabPanel } from 'devextreme-vue/tab-panel'

import { menuRoutesEnum } from '@/router'

import * as dataGrid from '@/composables/data-grid'
import { useNotify } from '@/composables/notify'
import { codeNameDisplayExpr } from '@/composables/label'

import { useAccountingStore } from '@/stores/accounting.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useColumns, laborPriceFields, laborPriceFieldNames, isProductFieldRequired } from '@/composables/registry/product-grid'
import { usePortalStore } from '@/stores/portal.store'
import { useMultiSelectFilter, useMultiSelectEditorOptions } from '@/composables/data-grid'
import MultiSelectFilter from '@/components/shared/MultiSelectFilter.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'

import ProductAccountingGrid from '@/components/registry/ProductAccountingGrid.vue'
import ProductWeightGrid from '@/components/registry/ProductWeightGrid.vue'
import ProductDocumentGrid from '@/components/registry/ProductDocumentGrid.vue'
import SupersessionProductDropDownBox from '@/components/shared/SupersessionProductDropDownBox.vue'
import SupplierBrandCreatePopup from '@/components/registry/SupplierBrandCreatePopup.vue'

import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'

import { useEntityLock } from '@/services/entity-lock.service'

const gridId = 'product-grid'

const i18n = getI18n()
const { gridRef, focusGrid } = dataGrid.useGridRef()
const { route, router } = dataGrid.useGridRouter()
const { notifyWarning } = useNotify()

const formInstance = ref(null)
const accountingStore = useAccountingStore()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const editors = {}

const createSupplierBrandOptionId = '__create_supplier_brand__'
const showSupplierBrandCreateModal = ref(false)

const getSupplierBrandOptions = () => [
  {
    id: createSupplierBrandOptionId,
    code: `${i18n.t('common.create')} +`,
    isCreateOption: true,
  },
  ...[...registryStore.supplierBrands].sort((a, b) => a.code.localeCompare(b.code)),
]

const openSupplierBrandCreateModal = () => {
  if (showSupplierBrandCreateModal.value) return

  editors['supplier_brand_ref']?.close()
  showSupplierBrandCreateModal.value = true
}

const onSupplierBrandCreated = (supplierBrand) => {
  const supplierBrandEditor = editors['supplier_brand_ref']
  supplierBrandEditor?.option('dataSource', getSupplierBrandOptions())
  supplierBrandEditor?.option('value', supplierBrand.id)
}

const supplierBrandItemTemplate = (itemData, _, itemElement) => {
  itemElement.textContent = itemData?.isCreateOption ? `${i18n.t('common.create')} +` : codeNameDisplayExpr(itemData)

  if (!itemData?.isCreateOption) return

  itemElement.classList.add('text-primary', 'text-weight-medium')
  itemElement.addEventListener('click', event => {
    event.preventDefault()
    event.stopPropagation()
    openSupplierBrandCreateModal()
  })
}

const currencyFields = ['price', 'price_vat', 'cost', 'cost_vat', ...laborPriceFieldNames]

const basePriceEditingItems = (productType) => [
  { dataField: 'currency_ref', editorOptions: { readOnly: true } },
  { dataField: 'vat_type_ref' },
  {
    dataField: 'price',
    label: { text: i18n.t('common.priceListProduct') },
    isRequired: isProductFieldRequired('price', productType),
    editorOptions: dataGrid.useCurrencyOptions(),
  },
  {
    dataField: 'price_vat',
    label: { text: i18n.t('common.priceVat') },
    isRequired: isProductFieldRequired('price_vat', productType),
    editorOptions: dataGrid.useCurrencyOptions(),
  },
]

const costEditingItems = () => [
  { dataField: 'cost', editorOptions: { ...dataGrid.useCurrencyOptions(), readOnly: true } },
  { dataField: 'cost_vat', editorOptions: { readOnly: true } },
]

const getMainInfoEditingItems = (productType) => {
  const basePriceItems = basePriceEditingItems(productType)

  switch (productType) {
    case registryStore.productTypes.PRODUCT:
      return [
        ...basePriceItems,
        { dataField: 'discount_category_ref' },
        { dataField: 'price_date', dataType: 'date' },
        { itemType: 'empty', colSpan: 2 },
        { dataField: 'unit_of_measure_ref' },
        { dataField: 'package_quantity', editorOptions: { readOnly: true } },
        { dataField: 'minimum_quantity', editorOptions: { readOnly: true } },
        { dataField: 'weight_kg', editorOptions: { format: '#0.## kg' } },
        { dataField: 'product_analytic_group_ref', editorOptions: { readOnly: true } },
        { dataField: 'product_typology_ref', editorOptions: { readOnly: true } },
        { dataField: 'product_manufacturer_family_ref', editorOptions: { readOnly: true } },
      ]
    case registryStore.productTypes.SERVICE:
      return [
        ...basePriceItems,
        { dataField: 'discount_category_ref' },
        { dataField: 'price_date', dataType: 'date' },
        ...costEditingItems(),
        { dataField: 'unit_of_measure_ref' },
        { itemType: 'empty', colSpan: 3 },
        { dataField: 'product_analytic_group_ref', editorOptions: { readOnly: true } },
        { dataField: 'product_typology_ref', editorOptions: { readOnly: true } },
        { dataField: 'product_manufacturer_family_ref', editorOptions: { readOnly: true } },
      ]
    case registryStore.productTypes.EXPENSE:
      return [
        ...basePriceItems,
        { dataField: 'value_percent' },
        { dataField: 'price_date', dataType: 'date' },
        ...costEditingItems(),
      ]
    default:
      return []
  }
}

const laborPriceEditingItems = ({ net, vat, netCaption }) => [
  { dataField: net, label: { text: i18n.t(netCaption) }, editorOptions: dataGrid.useCurrencyOptions() },
  { dataField: vat, label: { text: i18n.t('common.priceVat') }, editorOptions: dataGrid.useCurrencyOptions() },
]

const [internalLaborPrice, warrantyLaborPrice] = laborPriceFields

const laborMainInfoEditingItems = () => [
  ...basePriceEditingItems(registryStore.productTypes.LABOR),
  { itemType: 'empty' },
  { dataField: 'price_date', dataType: 'date' },
  ...costEditingItems(),
  { itemType: 'empty', colSpan: 2 },
  ...laborPriceEditingItems(internalLaborPrice),
  { dataField: 'unit_of_measure_ref', editorOptions: { readOnly: true } },
  { itemType: 'empty' },
  ...laborPriceEditingItems(warrantyLaborPrice),
]

const entityLock = useEntityLock({
  baseLockUrl: registryStore.api.getAbsoluteUrl() + registryStore.api.productsUrl,
  modelName: 'Product',
  gridRef,
})

const hasProductClaim = () => !webSiteStore.hasClaim(webSiteStore.claimsEnum.APP_MAW_PARTS)

const rowButtons = entityLock.getEntityLockButtons({
  visible: {
    delete: (e) => !e.isReadonly && !entityLock.isLocked(false, e.entityId)
  },
  hasClaim: hasProductClaim
})
rowButtons.push(
  {
    name: 'clone',
    text: i18n.t('common.clone'),
    icon: 'mdi-content-copy',
    class: ['dx-link', 'dx-icon', 'mdi', 'mdi-content-copy', 'dx-link-icon'],
    onClick: async(e) => {
      dataGrid.isSaveAndClone.value = true
      await registryStore.api.getProductDetailsAsync(e.entityId).then(res => {
        dataGrid.formData.value = res.data
        gridRef.value.instance.addRow()
      })
    },
    visibleFn: (e) => hasProductClaim() && !entityLock.isLocked(false, e.entityId),
  }
)

const tradeAccounts = accountingStore.getLocalizedTradeAccounts()
const gridDataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.productsUrl)

const columns = useColumns(editors)

const { defaultDialogTitle } = dataGrid.useDefaultDialogTitle()
const { applyFilter, setMultiSelectFilterRef, clearMultiSelectFilter } = useMultiSelectFilter()

const getEditingItems = (productType) => {
  const { PRODUCT, SERVICE, LABOR, EXPENSE } = registryStore.productTypes
  const mainInfoItems = productType === LABOR ? laborMainInfoEditingItems() : getMainInfoEditingItems(productType)
  const identifierItems = [{ dataField: 'product_type', editorOptions: { readOnly: true } }]

  if (productType === LABOR) identifierItems.push({ dataField: 'technical_qualification_id' })
  if ([PRODUCT, SERVICE].includes(productType)) {
    identifierItems.push({ dataField: 'product_family_ref', editorOptions: { readOnly: true } })
  }
  if (productType === EXPENSE) {
    identifierItems.push({ dataField: 'purchase_family_id', editorOptions: { readOnly: true } })
  }

  identifierItems.push(
    { dataField: 'supplier_brand_ref', editorOptions: { readOnly: true } },
    { dataField: 'code', editorOptions: { readOnly: true } },
    { dataField: 'name', colSpan: 2 },
  )

  if (productType === EXPENSE) identifierItems.push({ dataField: 'expense_type_id' })
  if (productType === PRODUCT) {
    identifierItems.push(
      { dataField: 'supplier_order_group_ref', editorOptions: { readOnly: true } },
      { dataField: 'product_availability', editorOptions: { readOnly: true } },
    )
  }

  const items = [
    {
      itemType: 'group',
      caption: i18n.t('common.identifier', 2),
      colCount: 2,
      items: identifierItems,
    },
  ]

  if (mainInfoItems.length) {
    items.push({
      itemType: 'group',
      name: 'mainInfoGroup',
      caption: i18n.t('common.mainInfo', 2),
      colSpan: 2,
      colCount: 4,
      items: mainInfoItems,
    })
  }

  return items
}

const editing = dataGrid.useDefaultEditing(i => defaultDialogTitle(i, i18n.t('common.product')), {
  colCount: 3,
  items: getEditingItems(null),
  onInitialized: (e) => { formInstance.value = e.component },
}, {
  isSaveAndNew: true,
  isSaveAndClone: true,
  onInitializedCustom: (e, hasClaim) => entityLock.readOnlyToolbarItems(e, hasClaim),
  entityLock: entityLock,
  allowDeleting: hasProductClaim(),
  allowUpdating: hasProductClaim(),
  allowAdding: hasProductClaim(),
  hasClaim: hasProductClaim,
})

const refreshEditingItems = (productType) => {
  gridRef.value?.instance?.option('editing.form.items', getEditingItems(productType))
}

const lastProductType = ref(null)
const lastSupplierBrandRef = ref(null)

const validCompanyDataAddressCountry = () => {
  if (!registryStore.companyData?.address?.country) {
    notifyWarning(i18n.t('common.missingAddress'), {
      timeout: window.config.NOTIFICATION_DURATION,
      actions: [
      {
        label: i18n.t('common.complete_action'), size: 'sm', color: 'white', handler: async() => {
          const route = { path: menuRoutesEnum.COMPANY }
          await router.push(route)
        }
      }
      ]
    })
    return false
  }
  return true
}

const onInitNewRow = (e) => {
  e.data = e.data || {}

  const isClone = !!e.data.product_type || !!e.data.supplier_brand_ref

  if (!isClone) {
    e.data.product_type = registryStore.productTypes.PRODUCT
    e.data.supplier_brand_ref = registryStore.companyData.default_supplier_brand_ref
    e.data.price = e.data.price_vat = 0
  } else {
    e.data.product_type = e.data.product_type ?? lastProductType.value ?? registryStore.productTypes.PRODUCT
    e.data.supplier_brand_ref = e.data.supplier_brand_ref ?? lastSupplierBrandRef.value ?? registryStore.companyData.default_supplier_brand_ref
  }

  if (e.data.product_type !== registryStore.productTypes.TEXT) {
    if (registryStore.companyData.default_currency_ref) {
      e.data.currency_ref = e.data.currency_ref || registryStore.companyData.default_currency_ref
    }
    if (registryStore.companyData.default_vat_type_ref) {
      e.data.vat_type_ref = e.data.vat_type_ref || registryStore.companyData.default_vat_type_ref
    }
  }

  if (registryStore.companyData.default_unit_of_measure_ref) {
    e.data.unit_of_measure_ref = registryStore.companyData.default_unit_of_measure_ref
  }

  e.data.show_in_schedule = e.data.show_in_schedule ?? false
  refreshEditingItems(e.data.product_type)
}

let firstEditLoad = true

const toggleReadOnlyFieldArray = [
  'code',
  'cost_vat',
  'cost',
  'currency_ref',
  'discount_category_ref',
  'minimum_quantity',
  'package_quantity',
  'price_date',
  'price_vat',
  'price',
  'product_analytic_group_ref',
  'product_availability',
  'product_manufacturer_family_ref',
  'product_typology_ref',
  'purchase_family_id',
  'show_in_schedule',
  'supplier_order_group_ref',
  'unit_of_measure_ref',
  'value_percent',
  'vat_type_ref',
  'weight_kg',
  ...laborPriceFieldNames,
]

const portalReadOnlyFields = [
  'code', 'currency_ref', 'minimum_quantity', 'package_quantity',
  'product_analytic_group_ref', 'product_availability', 'product_manufacturer_family_ref',
  'product_typology_ref', 'supplier_order_group_ref',
]

const expensePriceFields = ['cost', 'cost_vat', 'price', 'price_vat', 'vat_type_ref']
const expenseFieldsWithoutFamily = ['cost', 'cost_vat', 'price_vat', 'vat_type_ref', 'value_percent']
const { PRODUCT, SERVICE, LABOR, EXPENSE, TEXT } = registryStore.productTypes

const updateEditors = e => {
  e.editorOptions.onInitialized = args => { editors[e.dataField] = args.component }
}

const afterValueChanged = (e, callback) => {
  const onValueChanged = e.editorOptions.onValueChanged
  e.editorOptions.onValueChanged = args => {
    onValueChanged?.(args)
    callback(args)
  }
}

const onEditorPreparingToggleField = (e) => {
  if (e.parentType !== 'dataRow' || !toggleReadOnlyFieldArray.includes(e.dataField)) return

  updateEditors(e)

  const fieldName = e.dataField
  const productType = e.row.data?.product_type
  const isExpense = productType === registryStore.productTypes.EXPENSE
  const purchaseFamily = portalStore.getPurchaseFamilyById(e.row.data?.purchase_family_id)
  const isPercentageValue = purchaseFamily?.isPercentageValue

  if (isExpense && !purchaseFamily && expenseFieldsWithoutFamily.includes(fieldName)) {
    e.editorOptions.readOnly = true
    return
  }

  if (isExpense && expensePriceFields.includes(fieldName)) {
    e.editorOptions.readOnly = isPercentageValue === true
    return
  }

  if (fieldName === 'value_percent') {
    e.editorOptions.readOnly = !isExpense || isPercentageValue === false
    return
  }

  switch (fieldName) {
    case 'cost':
    case 'cost_vat':
      e.editorOptions.readOnly = [TEXT, PRODUCT].includes(productType)
      break
    case 'supplier_order_group_ref':
    case 'weight_kg':
    case 'package_quantity':
    case 'minimum_quantity':
      e.editorOptions.readOnly = [TEXT, SERVICE, LABOR, EXPENSE].includes(productType)
      break
    case 'product_availability':
      e.editorOptions.readOnly = [SERVICE, LABOR, EXPENSE].includes(productType)
      break
    case 'purchase_family_id':
      e.editorOptions.readOnly = [LABOR, PRODUCT, SERVICE, TEXT].includes(productType)
      break
    case 'unit_of_measure_ref':
      e.editorOptions.readOnly = [TEXT, LABOR].includes(productType)
      break
    case 'show_in_schedule':
      e.editorOptions.readOnly = productType !== LABOR
      break
    default:
      e.editorOptions.readOnly = productType === TEXT
  }

  if (portalReadOnlyFields.includes(fieldName)) {
    e.editorOptions.readOnly = e.row?.data?.is_portal || e.row.data?.portal_reference_id
  }
}

const filterItems = {
  product_type: () => registryStore.getLocalizedProductTypes(),
  product_family_ref: () => registryStore.getLocalizedProductFamilies(),
  purchase_family_id: () => portalStore.getLocalizedPurchaseFamilies(),
  product_availability: () => registryStore.getLocalizedProductAvailabilities(),
}

const getFilterDataSource = field => filterItems[field] ? [{ items: [...filterItems[field]()] }] : []

const onEditorPreparing = (e) => {
  if (e.parentType === 'filterRow' && filterItems[e.dataField]) {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: filterItems[e.dataField](),
      field: e.dataField,
    })
  }

  if (e.parentType === 'dataRow' && !['product_analytic_group_ref', 'product_typology_ref', 'product_manufacturer_family_ref'].includes(e.dataField) && !e.row.data?.id) {
    e.editorOptions.readOnly = false
  }
  if (e.parentType === 'dataRow' && e.dataField === 'currency_ref') {
    updateEditors(e)
    afterValueChanged(e, args => {
      const currentCurrency = webSiteStore.currencies.find(currency => currency.id === args.value)
      if (currentCurrency) {
        const format = dataGrid.useCurrencyOptions({ currency: currentCurrency }).format
        currencyFields.forEach(field => editors[field]?.option('format', format))
      }
    })
  }
  if (e.parentType === 'dataRow' && currencyFields.includes(e.dataField)) {
    updateEditors(e)
    const currentCurrency = webSiteStore.currencies.find((currency) => currency.id === e.row.data?.currency_ref)
    if (currentCurrency) {
      e.editorOptions.format = dataGrid.useCurrencyOptions({ currency: currentCurrency }).format
    }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'product_type') {
    updateEditors(e)
    lastProductType.value = e.row.data?.product_type
    afterValueChanged(e, args => {
      lastProductType.value = args.value
      if (args.value !== registryStore.productTypes.TEXT) {
        editors['price']?.option('value', 0)
        editors['price_vat']?.option('value', 0)
      }
      refreshEditingItems(args.value)
      formInstance.value?.repaint()
    })
  }

  if (e.parentType === 'dataRow' && e.dataField === 'purchase_family_id') {
    updateEditors(e)
    afterValueChanged(e, () => formInstance.value?.repaint())
  }

  if (e.parentType === 'dataRow' && e.dataField === 'supplier_brand_ref') {
    updateEditors(e)
    lastSupplierBrandRef.value = e.row.data?.supplier_brand_ref
    if (!e.row.data?.id) {
      e.editorOptions.dataSource = getSupplierBrandOptions()
      e.editorOptions.itemTemplate = supplierBrandItemTemplate
    }
    const onValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      if (args.value === createSupplierBrandOptionId) {
        args.component.option('value', args.previousValue ?? null)
        openSupplierBrandCreateModal()
        return
      }

      onValueChanged?.(args)
      lastSupplierBrandRef.value = args.value
      formInstance.value?.repaint()
    }
  }

  if (e.dataField === 'unit_of_measure_ref') {
    updateEditors(e)
    afterValueChanged(e, () => formInstance.value?.repaint())

    const productType = e.row?.data?.product_type
    let store = webSiteStore.getLocalizedUnitsOfMeasure()
    if (productType === registryStore.productTypes.LABOR) {
      store = store.filter(item => item.code === webSiteStore.unitOfMeasureOfTimeEnum.MN)
      e.row.data.unit_of_measure_ref = store.length > 0 ? store[0].id : null
    }
    e.editorOptions.dataSource = store
  }

  if (firstEditLoad && e.row?.data?.id) {
    firstEditLoad = false
    e.promise = registryStore.api.getProductDetailsAsync(e.row?.data?.id).then(res => {
      e.row.data = dataGrid.formData.value = res.data
      refreshEditingItems(res.data.product_type)
      formInstance.value?.repaint()
    })
  }

  if (e.parentType === 'dataRow' && (!hasProductClaim() || entityLock?.isReadOnly?.value)) {
    e.editorOptions.readOnly = true
  } else {
    onEditorPreparingToggleField(e)
  }
}

const onEditingStart = (e) => {
  firstEditLoad = true
  refreshEditingItems(e.data.product_type)
}

const onClearFilter = () => {
  clearMultiSelectFilter()
  router.replace()
}

const fieldMapping = {
  'name': 'NAME',
  'discount_category_ref': 'DISCOUNT_CATEGORY',
  'cost': 'COST',
  'price': 'PRICE',
  'internal_price': 'INTERNAL_PRICE',
  'warranty_price': 'WARRANTY_PRICE',
  'product_family_ref': 'PRODUCT_FAMILY',
  'unit_of_measure_ref': 'UNIT_OF_MEASURE',
  'vat_type_ref': 'VAT_TYPE',
  'weight_kg': 'WEIGHT_KG'
}

const isFieldModified = (modifiedFields, fieldName) => {
  return (modifiedFields & registryStore.productFieldMaskFlag[fieldName]) !== 0
}

const accountingFields = {
  product_accounting_intrastat_code: item => item.intrastat_code,
  product_accounting_trade_account_purchase_ref: item => tradeAccounts.find(x => x.id === item.trade_account_purchase_ref)?.name,
  product_accounting_trade_account_sale_ref: item => tradeAccounts.find(x => x.id === item.trade_account_sale_ref)?.name,
}

const onCellPrepared = (e) => {
  if (e.rowType !== 'data') return

  const field = e.column.dataField
  if (fieldMapping[field] && isFieldModified(e.data.modified_fields, fieldMapping[field])) {
    e.cellElement.classList.add('dx-cell-modified-field')
  }

  if (field === 'price' && e.data.price && e.data.previous_price) {
    if (e.data.price > e.data.previous_price) e.cellElement.classList.add('dx-cell-price-increased')
    if (e.data.price < e.data.previous_price) e.cellElement.classList.add('dx-cell-price-decreased')
  }

  if (accountingFields[field]) {
    const accounting = registryStore.productAccountings.find(item => item.product_ref === e.data.id)
    e.cellElement.textContent = accounting ? accountingFields[field](accounting) || '' : ''
  }

  if (e.column.command === 'expand' && e.data.product_type === registryStore.productTypes.TEXT) {
    e.cellElement.classList.add('dx-command-expand-hidden')
  }
}

const onRowPrepared = (e) => {
  if (e.rowType === 'data') {
    if (!e.data?.is_portal) {
      e.rowElement.classList.add('dx-row-dealer')
    }
    if (!e.data?.is_portal && e.data?.purchase_family_id && Object.values(portalStore.hiddenPurchaseFamilyCodes).includes(portalStore.getPurchaseFamilyById(e.data.purchase_family_id)?.code)) {
      e.rowElement.classList.add('dx-row-expense')
    }
  }
}

const onTabPanelSelectionChanged = () => { gridRef.value.instance.updateDimensions() }

const onTabPanelContentReady = (e) => {
  const firstVisibleTabIndex = e.component.option('items').findIndex(item => item.visible !== false)
  if (firstVisibleTabIndex >= 0) {
    e.component.option('selectedIndex', firstVisibleTabIndex)
  }
}

const onRowUpdating = (e) => {
  if (!e.newData?.price_list_ref) {
    e.newData.price_list = e.oldData.price_list_ref
    e.newData.price_list_ref = e.oldData.price_list_ref
  }
}

const onRowRemoved = async(e) => { await e.component.refresh() }

const { repaintRow } = dataGrid.useUpdateMasterDetail()

const onDetailRowSaved = (parentId, parentRowKey) => {
  repaintRow(gridRef, registryStore.api.getProductDetailsAsync, parentId, parentRowKey)
}

const SORT_RESET_FIELDS = ['supplier_brand_ref', 'code', 'name']

let sortingWasDisabled = false
let originalSortingMode = null

const hasActiveFilter = (grid) =>
  SORT_RESET_FIELDS.some((field) => {
    const value = grid.columnOption(field, 'filterValue')
    return value != null && value !== ''
  })

const clearSorting = (grid) => {
  grid.getVisibleColumns().forEach((col) => {
    if (col.sortOrder) {
      grid.columnOption(col.dataField, 'sortOrder', undefined)
      grid.columnOption(col.dataField, 'sortIndex', undefined)
    }
  })
}

const applyDefaultSort = (grid) => {
  grid.columnOption('supplier_brand_ref', 'sortOrder', 'asc')
  grid.columnOption('supplier_brand_ref', 'sortIndex', 0)
  grid.columnOption('code', 'sortOrder', 'asc')
  grid.columnOption('code', 'sortIndex', 1)
}

const onOptionChanged = (e) => {
  if (e.name !== 'columns') return

  const grid = e.component
  const filtered = hasActiveFilter(grid)

  if (e.fullName.includes('filterValue')) {
    if (filtered && !sortingWasDisabled) {
      clearSorting(grid)
      originalSortingMode = grid.option('sorting.mode')
      grid.option('sorting.mode', 'none')
      sortingWasDisabled = true
    } else if (!filtered && sortingWasDisabled) {
      grid.option('sorting.mode', originalSortingMode ?? 'multiple')
      applyDefaultSort(grid)
      sortingWasDisabled = false
    }
    return
  }

  if (!e.fullName.includes('sortOrder')) return

  if (filtered) return

  const supplierBrandIndex = grid.columnOption('supplier_brand_ref', 'sortIndex')

  if (grid.columnOption('supplier_brand_ref', 'sortOrder') && supplierBrandIndex === 0) return

  grid.getVisibleColumns().forEach((col) => {
    if (col.dataField !== 'supplier_brand_ref' && col.sortOrder) {
      const shouldIncrement = supplierBrandIndex == null || col.sortIndex < supplierBrandIndex
      if (shouldIncrement) grid.columnOption(col.dataField, 'sortIndex', col.sortIndex + 1)
    }
  })

  grid.columnOption('supplier_brand_ref', 'sortOrder', 'asc')
  grid.columnOption('supplier_brand_ref', 'sortIndex', 0)
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: editing,
  masterDetail: {
    enabled: hasProductClaim(),
    template: 'master-detail',
  },
  onInitNewRow: onInitNewRow,
  onEditorPreparing: onEditorPreparing,
  onCellPrepared: onCellPrepared,
  onRowPrepared: onRowPrepared,
  onEditingStart: onEditingStart,
  onClearFilter: onClearFilter,
  onOptionChanged: onOptionChanged,
  onRowUpdating: onRowUpdating,
  onRowRemoved: onRowRemoved,
  focusGrid: focusGrid,
  onToolbarPreparing: (e) => {
    dataGrid.useDefaultToolbar(e, {
      addButtonOptions: { hint: `${i18n.t('common.add')} ${i18n.t('common.product')}` },
    })
    e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
  },
  paging: {
    pageSize: 100,
    enabled: true
  },
  routerFilter: {
    fields: [{brand: 'supplier_brand_ref'}, {code: 'code'}],
    getRoute: () => route,
    router: router
  }
})

</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
      height="90vh"
    >
      <template #gridLayoutConfigTemplate>
        <grid-layout-config-template
          :grid-ext-ref="gridRef"
          :grid-ext-id="gridId"
        />
      </template>
      <template #master-detail="{ data: templateOptions }">
        <dx-tab-panel
          animation-enabled
          @selection-changed="onTabPanelSelectionChanged"
          @content-ready="onTabPanelContentReady"
        >
          <dx-item
            :visible="templateOptions.data.product_type === registryStore.productTypes.PRODUCT"
            :title="$t('common.ties', 2)"
            template="ties-tab"
          />
          <template #ties-tab>
            <supersession-product-drop-down-box
              v-if="templateOptions.data.product_type === registryStore.productTypes.PRODUCT"
              :data="{ supersession_id: templateOptions.data.id }"
              read-only
              show-tie-type
            />
          </template>
          <dx-item
            :visible="templateOptions.data.product_type !== registryStore.productTypes.TEXT"
            :title="$t('common.document', 2)"
            template="documents-tab"
          />
          <template #documents-tab>
            <product-document-grid
              v-if="templateOptions.data.product_type !== registryStore.productTypes.TEXT"
              :product="templateOptions.data"
            />
          </template>
          <dx-item
            :visible="templateOptions.data.product_type !== registryStore.productTypes.TEXT"
            :title="$t('common.accounting', 2)"
            template="accountings-tab"
          />
          <template #accountings-tab>
            <product-accounting-grid
              v-if="templateOptions.data.product_type !== registryStore.productTypes.TEXT"
              :product="templateOptions.data"
              :data="templateOptions"
              :entity-lock="entityLock"
              @row-saved="onDetailRowSaved"
            />
          </template>
          <dx-item
            :visible="templateOptions.data.product_type === registryStore.productTypes.PRODUCT"
            :title="$t('common.weightAndMeasure', 2)"
            template="weights-tab"
          />
          <template #weights-tab>
            <product-weight-grid
              v-if="templateOptions.data.product_type === registryStore.productTypes.PRODUCT"
              :product="templateOptions.data"
              :entity-lock="entityLock"
            />
          </template>
        </dx-tab-panel>
      </template>
      <template #typeCellTemplate="{ data: templateOptions }">
        <q-chip
          size="sm"
          class="q-ma-none"
          text-color="white"
          style="margin: 0"
          :label="registryStore.getLocalizedProductTypes().find(x => x.id === templateOptions.data.product_type)?.name"
          :style="{ backgroundColor: registryStore.getLocalizedProductTypes().find(x => x.id === templateOptions.data.product_type)?.color }"
        />
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'Product'"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="entityLock"
          :is-locked-available="true"
          :buttons="rowButtons"
        />
      </template>
      <template #customToolbarAddButtonTemplate>
        <custom-toolbar-add-button-template
          :grid="gridRef"
          :entity-lock="entityLock"
          :on-before-click="validCompanyDataAddressCountry"
        />
      </template>
      <template #multiSelectFilterRef="{ data }">
        <multi-select-filter
          :ref="el => setMultiSelectFilterRef(data.component.option('field'), el)"
          :data-source="getFilterDataSource(data.component.option('field'))"
          :field="data.component.option('field')"
          :grid-ref="gridRef"
          :apply-filter="applyFilter"
          v-bind="{ data }"
        />
      </template>
    </dx-data-grid>
    <supplier-brand-create-popup
      v-if="showSupplierBrandCreateModal"
      v-model:visible="showSupplierBrandCreateModal"
      @created="onSupplierBrandCreated"
    />
  </div>
</template>
